import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Music4, Pause, Play, AlertTriangle } from "lucide-react";
import { API, coverSrc } from "@/lib/creatorApi";
import { subscribe, toggleShowcase, seekShowcase, stopShowcase } from "@/lib/showcasePlayer";

const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;
const GRADIENTS = { "gospel-reggae": "from-emerald-500 to-lime-400", "gospel-rnb": "from-fuchsia-500 to-rose-400", worship: "from-sky-500 to-violet-500" };

function SampleCard({ song, player, i }) {
  const mine = player.key === song.key;
  const playing = mine && player.status === "playing";
  const loading = mine && player.status === "loading";
  const errored = mine && player.status === "error";
  const audioUrl = `${process.env.REACT_APP_BACKEND_URL}${song.audio_url}`;
  const cover = coverSrc(song);
  const gradient = GRADIENTS[song.style_key] || "from-blue-500 to-violet-600";
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.08, duration: 0.45 }}
      data-testid={`sample-card-${song.key}`}
      data-playing={playing ? "true" : "false"}
      className={`group relative overflow-hidden rounded-3xl border bg-[#0a0d1c]/85 p-5 transition-colors ${mine ? "border-blue-400/40" : "border-white/[0.1]"}`}
    >
      <div className="flex items-start gap-4">
        <button
          type="button"
          data-testid={`sample-play-${song.key}`}
          onClick={() => toggleShowcase(song.key, audioUrl)}
          aria-label={playing ? `Pause ${song.title}` : `Play ${song.title}`}
          aria-pressed={playing}
          className="relative h-24 w-24 flex-shrink-0 overflow-hidden rounded-2xl shadow-lg sm:h-28 sm:w-28"
        >
          {cover ? <img src={cover} alt="" className="h-full w-full object-cover" /> : <span className={`block h-full w-full bg-gradient-to-br ${gradient}`} />}
          <span className={`absolute inset-0 grid place-items-center bg-black/45 transition-opacity ${playing ? "opacity-100" : "opacity-90 group-hover:opacity-100"}`}>
            <span className="grid h-12 w-12 place-items-center rounded-full bg-white text-black shadow-[0_8px_24px_rgba(0,0,0,0.4)]">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : errored ? <AlertTriangle className="h-5 w-5 text-rose-600" /> : playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
            </span>
          </span>
        </button>
        <div className="min-w-0 flex-1">
          <span data-testid={`sample-style-${song.key}`} className={`inline-block rounded-full bg-gradient-to-r ${gradient} px-3 py-1 font-display text-[12px] font-semibold uppercase tracking-wider text-white`}>{song.style_label}</span>
          <h3 className="mt-2 truncate font-display text-lg font-semibold">{song.title}</h3>
          {song.lyrics_excerpt ? <p className="mt-1 line-clamp-2 text-[14px] leading-snug text-white/75">“{song.lyrics_excerpt}”</p> : null}
          <p className="mt-2 text-[13px] text-white/70">
            {errored ? "Couldn't play this sample right now." : mine && player.duration ? `${fmt(player.position)} / ${fmt(player.duration)}` : song.duration_sec ? fmt(song.duration_sec) : "Made in ELPM Studio"}
          </p>
        </div>
      </div>
      <div
        role="slider" aria-label="Seek" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((mine ? player.progress : 0) * 100)} tabIndex={0}
        data-testid={`sample-progress-${song.key}`}
        onClick={(e) => { if (!mine) return; const r = e.currentTarget.getBoundingClientRect(); seekShowcase((e.clientX - r.left) / r.width); }}
        onKeyDown={(e) => { if (!mine) return; if (e.key === "ArrowRight") seekShowcase(Math.min(1, player.progress + 0.05)); if (e.key === "ArrowLeft") seekShowcase(Math.max(0, player.progress - 0.05)); }}
        className={`mt-4 h-2 w-full overflow-hidden rounded-full bg-white/[0.1] ${mine ? "cursor-pointer" : ""}`}
      >
        <div className={`h-full rounded-full bg-gradient-to-r ${gradient} transition-[width] duration-200`} style={{ width: `${(mine ? player.progress : 0) * 100}%` }} />
      </div>
      <Link
        to={`/create/song?style=${encodeURIComponent(song.style_key)}`}
        data-testid={`sample-make-${song.key}`}
        className="mt-4 inline-flex items-center gap-1.5 font-display text-[14px] font-semibold text-blue-200 transition-colors hover:text-white"
      >
        Make one like this <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </Link>
    </motion.article>
  );
}

/** "Hear what ELPM can make" — three playable example songs from /api/showcase. */
export default function ShowcaseSection() {
  const [songs, setSongs] = useState(null);
  const [player, setPlayer] = useState({ key: null, status: "idle", progress: 0 });

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/showcase`).then(({ data }) => { if (alive) setSongs(Array.isArray(data) ? data : []); }).catch(() => { if (alive) setSongs([]); });
    const unsub = subscribe(setPlayer);
    return () => { alive = false; unsub(); stopShowcase(); };
  }, []);

  if (songs && songs.length === 0) return null;

  return (
    <section data-testid="home-showcase" className="mt-16">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-display text-[13px] font-semibold uppercase tracking-[0.22em] text-blue-200"><Music4 className="h-4 w-4" /> Hear what ELPM can make</p>
          <h2 className="mt-2 font-display text-2xl font-bold sm:text-3xl">Real songs made here — press play.</h2>
        </div>
        <p className="max-w-md text-[14px] text-white/75">Every example was created from lyrics and a style, exactly the way you'll do it. No studio, no instruments.</p>
      </div>
      {!songs ? (
        <div className="flex items-center gap-3 text-white/75"><Loader2 className="h-5 w-5 animate-spin" /> Loading examples…</div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          {songs.map((s, i) => <SampleCard key={s.key} song={s} player={player} i={i} />)}
        </div>
      )}
    </section>
  );
}
